'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

export interface LocationSelection {
  country?: { id: string; name: string; code: string; flag_emoji?: string } | null;
  province?: { id: string; name: string; name_np?: string } | null;
  district?: { id: string; name: string; name_np?: string } | null;
  city?: { id: string; name: string; city_type?: string } | null;
  ward?: { id: string; ward_number: number; name?: string } | null;
}

interface LocationBrowserProps {
  value?: LocationSelection;
  onChange?: (selection: LocationSelection) => void;
  /** Which levels to show. Defaults to all 5 levels */
  levels?: ('country' | 'province' | 'district' | 'city' | 'ward')[];
  /** Lock country to Nepal only */
  nepalOnly?: boolean;
  /** Compact single-row layout */
  compact?: boolean;
  /** Label prefix for accessibility */
  labelPrefix?: string;
  className?: string;
}

interface Country { id: string; name: string; code: string; flag_emoji?: string }
interface Province { id: string; name: string; name_np?: string }
interface District { id: string; name: string; name_np?: string }
interface City { id: string; name: string; city_type?: string }
interface Ward { id: string; ward_number: number; name?: string }

const CITY_TYPE_LABELS: Record<string, string> = {
  metropolitan: 'Metro',
  sub_metropolitan: 'Sub-Metro',
  municipality: 'Municipality',
  rural_municipality: 'Rural Municipality',
};

export default function LocationBrowser({
  value,
  onChange,
  levels = ['country', 'province', 'district', 'city', 'ward'],
  nepalOnly = false,
  compact = false,
  labelPrefix = '',
  className = '',
}: LocationBrowserProps) {
  const supabase = createClient();

  const [countries, setCountries] = useState<Country[]>([]);
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [wards, setWards] = useState<Ward[]>([]);

  const [sel, setSel] = useState<LocationSelection>(value || {});
  const [loading, setLoading] = useState<Record<string, boolean>>({});

  const show = (level: string) => levels.includes(level as any);

  // Load countries on mount
  useEffect(() => {
    if (!show('country')) return;
    setLoading(l => ({ ...l, country: true }));
    const query = supabase.from('loc_countries').select('id, name, code, flag_emoji').eq('is_active', true).order('name');
    if (nepalOnly) {
      query.eq('code', 'NP');
    }
    query.then(({ data }) => {
      setCountries(data || []);
      setLoading(l => ({ ...l, country: false }));
      // Auto-select Nepal if nepalOnly
      if (nepalOnly && data && data.length > 0 && !sel.country) {
        const nepal = data[0];
        const next = { country: nepal };
        setSel(next);
        onChange?.(next);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nepalOnly]);

  // Load provinces when country selected
  useEffect(() => {
    if (!show('province') || !sel.country) { setProvinces([]); return; }
    setLoading(l => ({ ...l, province: true }));
    supabase.from('loc_provinces').select('id, name, name_np').eq('country_id', sel.country.id).eq('is_active', true).order('name')
      .then(({ data }) => { setProvinces(data || []); setLoading(l => ({ ...l, province: false })); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.country?.id]);

  // Load districts when province selected
  useEffect(() => {
    if (!show('district') || !sel.province) { setDistricts([]); return; }
    setLoading(l => ({ ...l, district: true }));
    supabase.from('loc_districts').select('id, name, name_np').eq('province_id', sel.province.id).eq('is_active', true).order('name')
      .then(({ data }) => { setDistricts(data || []); setLoading(l => ({ ...l, district: false })); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.province?.id]);

  // Load cities when district selected
  useEffect(() => {
    if (!show('city') || !sel.district) { setCities([]); return; }
    setLoading(l => ({ ...l, city: true }));
    supabase.from('loc_cities').select('id, name, name_np, city_type').eq('district_id', sel.district.id).eq('is_active', true).order('name')
      .then(({ data }) => { setCities(data || []); setLoading(l => ({ ...l, city: false })); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.district?.id]);

  // Load wards when city selected
  useEffect(() => {
    if (!show('ward') || !sel.city) { setWards([]); return; }
    setLoading(l => ({ ...l, ward: true }));
    supabase.from('loc_wards').select('id, ward_number, name').eq('city_id', sel.city.id).eq('is_active', true).order('ward_number')
      .then(({ data }) => { setWards(data || []); setLoading(l => ({ ...l, ward: false })); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel.city?.id]);

  const handleCountryChange = useCallback((id: string) => {
    const country = countries.find(c => c.id === id) || null;
    const next: LocationSelection = { country };
    setSel(next);
    onChange?.(next);
  }, [countries, onChange]);

  const handleProvinceChange = useCallback((id: string) => {
    const province = provinces.find(p => p.id === id) || null;
    const next: LocationSelection = { country: sel.country, province };
    setSel(next);
    onChange?.(next);
  }, [provinces, sel.country, onChange]);

  const handleDistrictChange = useCallback((id: string) => {
    const district = districts.find(d => d.id === id) || null;
    const next: LocationSelection = { country: sel.country, province: sel.province, district };
    setSel(next);
    onChange?.(next);
  }, [districts, sel.country, sel.province, onChange]);

  const handleCityChange = useCallback((id: string) => {
    const city = cities.find(c => c.id === id) || null;
    const next: LocationSelection = { country: sel.country, province: sel.province, district: sel.district, city };
    setSel(next);
    onChange?.(next);
  }, [cities, sel.country, sel.province, sel.district, onChange]);

  const handleWardChange = useCallback((id: string) => {
    const ward = wards.find(w => w.id === id) || null;
    const next: LocationSelection = { ...sel, ward };
    setSel(next);
    onChange?.(next);
  }, [wards, sel, onChange]);

  const selectClass = `w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-snow text-sm 
    focus:outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-colors
    disabled:opacity-40 disabled:cursor-not-allowed appearance-none`;

  const labelClass = 'block text-snow/50 text-xs font-medium mb-1';

  const SelectWrapper = ({ children, label }: { children: React.ReactNode; label: string }) => (
    <div className={compact ? 'flex-1 min-w-0' : 'w-full'}>
      {!compact && <label className={labelClass}>{label}</label>}
      <div className="relative">
        {children}
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          <svg className="w-3.5 h-3.5 text-snow/40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`${compact ? 'flex flex-wrap gap-2 items-end' : 'space-y-3'} ${className}`}>
      {/* Country */}
      {show('country') && !nepalOnly && (
        <SelectWrapper label={`${labelPrefix}Country`}>
          <select
            aria-label={`${labelPrefix}Country`}
            value={sel.country?.id || ''}
            onChange={e => handleCountryChange(e.target.value)}
            disabled={loading.country}
            className={selectClass}
          >
            <option value="">{loading.country ? 'Loading…' : '🌍 Select Country'}</option>
            {countries.map(c => (
              <option key={c.id} value={c.id}>{c.flag_emoji} {c.name}</option>
            ))}
          </select>
        </SelectWrapper>
      )}

      {/* Province */}
      {show('province') && (
        <SelectWrapper label={`${labelPrefix}Province`}>
          <select
            aria-label={`${labelPrefix}Province`}
            value={sel.province?.id || ''}
            onChange={e => handleProvinceChange(e.target.value)}
            disabled={!sel.country || loading.province}
            className={selectClass}
          >
            <option value="">{loading.province ? 'Loading…' : 'All Provinces'}</option>
            {provinces.map(p => (
              <option key={p.id} value={p.id}>{p.name}{p.name_np ? ` · ${p.name_np}` : ''}</option>
            ))}
          </select>
        </SelectWrapper>
      )}

      {/* District */}
      {show('district') && (
        <SelectWrapper label={`${labelPrefix}District`}>
          <select
            aria-label={`${labelPrefix}District`}
            value={sel.district?.id || ''}
            onChange={e => handleDistrictChange(e.target.value)}
            disabled={!sel.province || loading.district}
            className={selectClass}
          >
            <option value="">{loading.district ? 'Loading…' : 'All Districts'}</option>
            {districts.map(d => (
              <option key={d.id} value={d.id}>{d.name}{d.name_np ? ` · ${d.name_np}` : ''}</option>
            ))}
          </select>
        </SelectWrapper>
      )}

      {/* City / Municipality */}
      {show('city') && (
        <SelectWrapper label={`${labelPrefix}City / Municipality`}>
          <select
            aria-label={`${labelPrefix}City`}
            value={sel.city?.id || ''}
            onChange={e => handleCityChange(e.target.value)}
            disabled={!sel.district || loading.city}
            className={selectClass}
          >
            <option value="">{loading.city ? 'Loading…' : 'All Cities'}</option>
            {cities.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}{c.city_type ? ` (${CITY_TYPE_LABELS[c.city_type] || c.city_type})` : ''}
              </option>
            ))}
          </select>
        </SelectWrapper>
      )}

      {/* Ward */}
      {show('ward') && (
        <SelectWrapper label={`${labelPrefix}Ward`}>
          <select
            aria-label={`${labelPrefix}Ward`}
            value={sel.ward?.id || ''}
            onChange={e => handleWardChange(e.target.value)}
            disabled={!sel.city || loading.ward}
            className={selectClass}
          >
            <option value="">{loading.ward ? 'Loading…' : 'All Wards'}</option>
            {wards.map(w => (
              <option key={w.id} value={w.id}>Ward {w.ward_number}{w.name ? ` – ${w.name}` : ''}</option>
            ))}
          </select>
        </SelectWrapper>
      )}
    </div>
  );
}

/** Utility: build a human-readable location string from a selection */
export function formatLocationSelection(sel: LocationSelection): string {
  const parts: string[] = [];
  if (sel.ward) parts.push(`Ward ${sel.ward.ward_number}`);
  if (sel.city) parts.push(sel.city.name);
  if (sel.district) parts.push(sel.district.name);
  if (sel.province) parts.push(sel.province.name);
  if (sel.country) parts.push(sel.country.name);
  return parts.join(', ');
}
